import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import './checkInDialog.css';

export interface MarkPaidDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (paymentReference: string) => Promise<void>;
}

/**
 * Records a payment against the session. On-system modal shell (shared
 * checkInDialog.css) — reference input + primary confirm.
 */
export const MarkPaidDialog: React.FC<MarkPaidDialogProps> = ({ open, onClose, onConfirm }) => {
  const [ref, setRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    if (submitting) return;
    setRef('');
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!ref.trim()) {
      setError('Payment reference is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(ref.trim());
      setRef('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mark paid');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) handleClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label="Mark Paid">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Mark Paid</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          <div className="ci-dialog__desc">
            Enter the EFTPOS receipt number, Xero payment ID, or bank transfer reference.
          </div>

          <label className="ci-dialog__field-label" htmlFor="mark-paid-ref">
            Payment reference
          </label>
          <input
            id="mark-paid-ref"
            ref={inputRef}
            className={`ci-dialog__input${error ? ' ci-dialog__input--error' : ''}`}
            placeholder="e.g. EFTPOS 004821"
            value={ref}
            onChange={e => {
              setRef(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSubmit();
            }}
            disabled={submitting}
          />

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button type="button" className="ci-dialog__cancel" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Saving…' : 'Mark Paid'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
